/**
 * Get the natural dimensions of an image file
 * @param file - The image file to analyze
 * @returns Promise that resolves to the width and height of the image
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
            URL.revokeObjectURL(objectUrl); // Clean up
        };
        img.onerror = () => {
            reject(new Error("Failed to load image dimensions"));
            URL.revokeObjectURL(objectUrl); // Clean up
        };
        img.src = objectUrl;
    });
}

export async function resizeImage(
    file: File,
    maxWidth: number,
    maxHeight: number,
    quality: number = 0.9,
    mimeType: string = "image/jpeg"
): Promise<File> {
    // First, create a bitmap to get original dimensions
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const ratio = Math.min(maxWidth / width, maxHeight / height);

    // Only resize if we need to make it smaller (ratio < 1)
    if (ratio >= 1) {
        bitmap.close();
        return file;
    }

    // Calculate new dimensions maintaining aspect ratio
    const newWidth = Math.round(width * ratio);
    const newHeight = Math.round(height * ratio);

    bitmap.close(); // Free memory

    // Create resized bitmap
    const resized = await createImageBitmap(file, {
        resizeWidth: newWidth,
        resizeHeight: newHeight,
        resizeQuality: "high"
    });

    // Draw to canvas to export as Blob
    const canvas = new OffscreenCanvas(newWidth, newHeight);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Image resize failed: cannot get canvas context");
    ctx.drawImage(resized, 0, 0);
    resized.close();

    // convert canvas to Blob and return File
    const blob = await canvas.convertToBlob({ type: mimeType, quality });
    return new File([blob], file.name, { type: mimeType });
}
